import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, useTheme, Theme } from '@mui/material';
import { Styles } from 'common/types/styles';
import { ReactElement, ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';

interface StylesProps {
  borderBottom?: boolean;
  paddingless?: boolean;
  rounded?: boolean;
  theme: Theme;
}

const getStyles = ({ borderBottom, paddingless, rounded, theme }: StylesProps): Styles => ({
  dialog: {
    borderRadius: rounded ? undefined : '1px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2, 2, 3),
    boxSizing: 'border-box',
    borderBottom: borderBottom ? '1px solid' : 'none',
    borderColor: 'divider',
  },
  title: {
    fontSize: 21,
    p: 0,
    width: '90%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  content: { padding: 0 },
  contentWrapper: { padding: paddingless ? 0 : theme.spacing(0, 3, 3) },
  close: { marginLeft: 0.5, color: theme.palette.text.primary },
});

interface Props {
  title?: string;
  children: ReactNode;
  open: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  scrollType?: 'paper' | 'body';
  hiddenHeader?: boolean;
  borderBottom?: boolean;
  paddingless?: boolean;
  rounded?: boolean;
  footer?: ReactNode;
  onClose: () => void;
}

export function Modal({
  title,
  children,
  open,
  onClose,
  scrollType,
  maxWidth = 'sm',
  footer,
  hiddenHeader = false,
  borderBottom = false,
  paddingless = false,
  rounded = false,
}: Props): ReactElement {
  const theme = useTheme();
  const styles = getStyles({ borderBottom, paddingless, rounded, theme });

  return (
    <Dialog
      maxWidth={maxWidth}
      fullWidth
      scroll={scrollType}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: styles.dialog }}
    >
      {!hiddenHeader && (
        <Box sx={styles.titleContainer}>
          <DialogTitle sx={styles.title}>{title}</DialogTitle>
          <IconButton sx={styles.close} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <DialogContent sx={styles.content}>
        <Box sx={styles.contentWrapper}>{children}</Box>
      </DialogContent>
      {footer && <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>{footer}</DialogActions>}
    </Dialog>
  );
}
